import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ArrowLeftIcon,
  ClipboardIcon,
  CheckIcon,
} from "@heroicons/react/24/outline";
import { useNavigate } from "react-router-dom";
import RepresentativeEditModal from "./RepresentativeEditModal";
import { PencilIcon } from "@heroicons/react/24/solid";

export default function AdminRepresentativesTable() {
  const { t } = useTranslation();
  const [representatives, setRepresentatives] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [copiedCode, setCopiedCode] = useState(null);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [repToEdit, setRepToEdit] = useState(null);

  useEffect(() => {
    loadRepresentatives();
  }, []);

  const loadRepresentatives = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/Representative/all`
      );

      if (!response.ok) throw new Error("Erreur chargement représentants");

      const reps = await response.json();
      setRepresentatives(reps);
    } catch (err) {
      console.error(err);
      setError("Erreur lors du chargement des représentants.");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (rep) => {
    setRepToEdit(rep);
    setEditModalOpen(true);
  };

  const handleCopy = (code) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(null), 1500);
  };

  return (
    <div className="p-6 space-y-6 text-primary">
      <button
        onClick={() => navigate(-1)}
        className="btn bg-primary text-white rounded-full flex items-center gap-2"
      >
        <ArrowLeftIcon className="w-5 h-5" />
        {t("global.back")}
      </button>

      <div className="flex flex-col md:flex-row justify-between items-center gap-4">
        <h1 className="text-2xl font-bold">{t("Liste des représentants")}</h1>
        <input
          type="text"
          placeholder="Rechercher par nom, prénom ou code..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="input input-bordered w-full md:w-64"
        />
      </div>

      {loading ? (
        <div className="text-center">Chargement...</div>
      ) : error ? (
        <div className="text-center text-red-500">{error}</div>
      ) : representatives.length === 0 ? (
        <div className="text-center text-gray-500">
          Aucun représentant trouvé.
        </div>
      ) : (
        <div className="overflow-x-auto border rounded-lg bg-base-100 shadow-sm">
          <table className="table table-zebra w-full">
            <thead>
              <tr>
                <th>Nom</th>
                <th>Prénom</th>
                <th>Code</th>
                <th>Profil public</th>
                <th>Affiché</th>
                <th>Parrain</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {representatives
                .filter(
                  (r) =>
                    r.firstName.toLowerCase().includes(search.toLowerCase()) ||
                    r.lastName.toLowerCase().includes(search.toLowerCase()) ||
                    r.code.toLowerCase().includes(search.toLowerCase())
                )
                .map((rep) => (
                  <tr key={rep.id}>
                    <td>{rep.lastName}</td>
                    <td>{rep.firstName}</td>
                    <td className="font-mono">{rep.code}</td>
                    <td className="text-sm text-gray-700">
                      {rep.city || ""}
                      {rep.city && <br />}
                      {rep.publicCity || ""}
                      {rep.publicCity && <br />}
                      {rep.phone_number || ""}
                      {rep.phone_number && <br />}
                      {rep.email || ""}
                    </td>
                    <td>
                      {rep.isActive ? (
                        <CheckIcon className="w-5 h-5 text-green-500" />
                      ) : (
                        <span className="text-red-500">Non</span>
                      )}
                    </td>
                    <td>{rep.parentName || "—"}</td>
                    <td>
                      <button
                        onClick={() => handleCopy(rep.code)}
                        className="btn btn-sm btn-ghost"
                        title="Copier le code"
                      >
                        {copiedCode === rep.code ? (
                          <CheckIcon className="w-5 h-5 text-green-500" />
                        ) : (
                          <ClipboardIcon className="w-5 h-5 text-gray-700" />
                        )}
                      </button>
                      <button
                        onClick={() => handleEdit(rep)}
                        className="btn btn-sm btn-ghost"
                        title="Consulter / modifier le représentant"
                      >
                        <PencilIcon className="w-5 h-5  text-gray-700" />
                      </button>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      )}
      {editModalOpen && repToEdit && (
        <RepresentativeEditModal
          rep={repToEdit}
          onClose={() => setEditModalOpen(false)}
          onSave={loadRepresentatives}
          allRepresentatives={representatives}
        />
      )}
    </div>
  );
}
